import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Search, ShoppingBag, Heart, User, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { label: "SHOP", to: "/shop" },
  { label: "NEW DROP", to: "/shop?badge=new" },
  { label: "BEST SELLERS", to: "/shop?badge=best_seller" },
  { label: "LIMITED", to: "/shop?badge=limited" },
  { label: "SALE", to: "/shop?on_sale=1" },
];

export default function Header({ announcement }) {
  const { count, setOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/shop?search=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setQ("");
    }
  };

  return (
    <>
      {announcement && (
        <div className="bg-[#FF2E4C] text-white overflow-hidden py-2">
          <div className="ticker-track">
            {[0, 1].map((k) => (
              <span key={k} className="font-mono text-[11px] tracking-widest uppercase px-8">
                {announcement} · {announcement} · {announcement}
              </span>
            ))}
          </div>
        </div>
      )}

      <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "glass border-b border-zinc-800" : "bg-[#0A0A0B]"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center gap-2 lg:hidden">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <button data-testid="mobile-menu-toggle" className="p-2 -ml-2" aria-label="menu">
                    <Menu className="w-6 h-6" />
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="bg-[#0A0A0B] border-zinc-800 w-[80vw] max-w-sm p-0">
                  <div className="p-6">
                    <span className="font-display text-2xl font-extrabold tracking-tight">SKG<span className="text-[#FF2E4C]">WEAR</span></span>
                    <nav className="mt-8 flex flex-col gap-1">
                      {NAV.map((n) => (
                        <Link key={n.label} to={n.to} onClick={() => setMobileOpen(false)}
                          className="py-3 font-display font-bold text-lg uppercase tracking-tight border-b border-zinc-800 hover:text-[#FF2E4C] transition-colors">
                          {n.label}
                        </Link>
                      ))}
                      <Link to={user && user.role === "admin" ? "/admin" : "/account"} onClick={() => setMobileOpen(false)}
                        className="py-3 font-display font-bold text-lg uppercase tracking-tight border-b border-zinc-800 hover:text-[#FF2E4C]">
                        {user ? "ΛΟΓΑΡΙΑΣΜΟΣ" : "ΣΥΝΔΕΣΗ"}
                      </Link>
                      <Link to="/contact" onClick={() => setMobileOpen(false)}
                        className="py-3 font-display font-bold text-lg uppercase tracking-tight border-b border-zinc-800 hover:text-[#FF2E4C]">
                        ΕΠΙΚΟΙΝΩΝΙΑ
                      </Link>
                    </nav>
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            <Link to="/" data-testid="nav-logo-skgwear" className="font-display text-xl sm:text-2xl font-extrabold tracking-tight lg:flex-none">
              SKG<span className="text-[#FF2E4C]">WEAR</span>
            </Link>

            <nav className="hidden lg:flex items-center gap-7 absolute left-1/2 -translate-x-1/2">
              {NAV.map((n) => (
                <Link key={n.label} to={n.to} data-testid={`nav-link-${n.label.toLowerCase().replace(/ /g, "-")}`}
                  className="font-display font-bold text-sm uppercase tracking-tight text-zinc-300 hover:text-[#FF2E4C] transition-colors">
                  {n.label}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-1 sm:gap-2">
              <button data-testid="search-toggle" onClick={() => setSearchOpen((v) => !v)} className="p-2 hover:text-[#FF2E4C] transition-colors" aria-label="search">
                <Search className="w-5 h-5" />
              </button>
              <Link to="/wishlist" data-testid="nav-link-wishlist" className="p-2 relative hover:text-[#FF2E4C] transition-colors" aria-label="wishlist">
                <Heart className="w-5 h-5" />
                {wishCount > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#FF2E4C] text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{wishCount}</span>}
              </Link>
              <Link to={user && user.role === "admin" ? "/admin" : "/account"} data-testid="nav-link-account" className="p-2 hover:text-[#FF2E4C] transition-colors hidden sm:block" aria-label="account">
                <User className="w-5 h-5" />
              </Link>
              <button data-testid="cart-drawer-toggle" onClick={() => setOpen(true)} className="p-2 relative hover:text-[#FF2E4C] transition-colors" aria-label="cart">
                <ShoppingBag className="w-5 h-5" />
                {count > 0 && <span data-testid="cart-item-count-badge" className="absolute -top-0.5 -right-0.5 bg-[#FF2E4C] text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{count}</span>}
              </button>
            </div>
          </div>

          {searchOpen && (
            <form onSubmit={submitSearch} className="pb-4 animate-fade">
              <div className="flex items-center gap-2 border-b-2 border-[#FF2E4C] pb-2">
                <Search className="w-5 h-5 text-zinc-400" />
                <input data-testid="search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Αναζήτηση προϊόντων..." className="flex-1 bg-transparent outline-none text-lg placeholder:text-zinc-600" />
                <button type="button" onClick={() => setSearchOpen(false)}><X className="w-5 h-5 text-zinc-400" /></button>
              </div>
            </form>
          )}
        </div>
      </header>
    </>
  );
}
